import { AmputationType } from '@/constants/order/amputationType';
import { ProsthesisCategory } from '@/store/models/ProsthesisCategory';

/**
 * Kafle ekranu głównego opisują POZIOM AMPUTACJI, nie rodzaj produktu (decyzja Ownera, GLA-59).
 *
 * Kafel ≠ kategoria w bazie. Transhumeral i Shoulder Disarticulation prowadzą do tej samej
 * kategorii produktów `glaze-above` — dzięki temu obserwator zamówień, mapy norm, SKU i warunek
 * leja finalnego działają bez zmian. Rozjazd jest świadomy i spisany w GLA-59; nie „porządkuj" go
 * bez przeczytania tamtej decyzji. Docelowe uporządkowanie taksonomii: GLA-65.
 */
export const CATEGORY_SLUG_BELOW = 'glaze-below';
export const CATEGORY_SLUG_ABOVE = 'glaze-above';
export const CATEGORY_SLUG_TERMINAL_ONLY = 'terminal-device-only';

/** Slug kafla, który NIE istnieje jako kategoria w bazie — żyje wyłącznie we froncie. */
export const TILE_SLUG_SHOULDER_DISARTICULATION = 'shoulder-disarticulation';

/**
 * Poziom amputacji zapisywany na zamówieniu przy jego zakładaniu (`initial_amputation_type`).
 *
 * Transradial świadomie BEZ wartości: enum `AmputationType` ma tylko dwie wartości, a ścieżka
 * Below i tak nie pyta o poziom amputacji — formularz pokazuje wtedy domyślny zestaw punktów
 * pomiarowych (`illustrationType === 'default'`), a `ViewOrderClientsDetails` pomija to pole
 * w zapisie. Dorobienie trzeciej wartości to osobna zmiana (GLA-59, punkt 4).
 */
export function initialAmputationTypeForTile(tileSlug: string): AmputationType | undefined {
	switch (tileSlug) {
		case CATEGORY_SLUG_ABOVE:
			return AmputationType.TranshumeralAmputation;
		case TILE_SLUG_SHOULDER_DISARTICULATION:
			return AmputationType.ShoulderDisarticulation;
		default:
			return undefined;
	}
}

/**
 * Grafiki kafla barkowego (Aleks, GLA-66 — 389 / 778 / 1167 px).
 *
 * Leżą we froncie (`ui/public/img/acro/`), a nie w treści statycznej, bo ten kafel jest
 * SYNTETYCZNY — nie ma go w `prosthesis-categories.json`, więc synchronizator nie miałby
 * wpisu, przy którym skopiowałby pliki do storage. Konwencja jest ta sama co dla kafli typów
 * rąk (`@/constants/hands/constants.ts`).
 */
const SHOULDER_DISARTICULATION_TILE_IMAGE = {
	image_1x: '/img/acro/shoulder-disarticulation.png',
	image_2x: '/img/acro/shoulder-disarticulation@2x.png',
	image_3x: '/img/acro/shoulder-disarticulation@3x.png',
};

/**
 * Syntetyczny kafel Shoulder Disarticulation. Nosi identyfikator kategorii `glaze-above`,
 * więc lista produktów filtruje się tak samo jak dla Transhumeral — różni je tylko slug,
 * po którym rozpoznajemy poziom amputacji.
 */
export function shoulderDisarticulationTile(aboveCategoryId: number): ProsthesisCategory {
	return {
		prosthesis_category_id: aboveCategoryId,
		name: 'Shoulder Disarticulation',
		slug: TILE_SLUG_SHOULDER_DISARTICULATION,
		excerpt: 'Above elbow devices for shoulder disarticulation.',
		image_uri: SHOULDER_DISARTICULATION_TILE_IMAGE,
	};
}

/**
 * Lista kafli ekranu głównego: kategorie z katalogu, BEZ „Terminal Device Only",
 * plus syntetyczny kafel wyłuszczenia.
 *
 * Kafel wyłuszczenia dokładamy dopiero, gdy kategoria Above jest wczytana — bez jej
 * identyfikatora lista produktów po kliknięciu byłaby pusta.
 *
 * ODSIANIE „Terminal Device Only" jest REGUŁĄ, nie filtrem w widoku (GLA-5). Kategoria WCIĄŻ
 * JEST w katalogu i przychodzi z API, więc jedyne, co dzieli protetyka od tej ścieżki, to ta
 * jedna linia — a ścieżka „po prostu nie działa" (zgłoszenie Alicji) i do GLA-61 kończyła się
 * twardym 500 przy składaniu zamówienia, bo nie ma w niej ani jednego ekranu danych leja.
 *
 * Reguła siedzi tutaj, a nie w `computed` ekranu, żeby dało się ją sprawdzić testem zachowania:
 * w widoku była jedną linią, której usunięcie nic by nie zapaliło.
 */
export function tilesForCategories(fromCatalog: ProsthesisCategory[]): ProsthesisCategory[] {
	const oferowane = fromCatalog.filter(c => c.slug !== CATEGORY_SLUG_TERMINAL_ONLY);
	const above = oferowane.find(c => c.slug === CATEGORY_SLUG_ABOVE);

	return above
		? [...oferowane, shoulderDisarticulationTile(above.prosthesis_category_id)]
		: oferowane;
}

/**
 * Poziom amputacji pokazywany w Customer details.
 *
 * Kolejność jest istotna klinicznie: zapisany wybór protetyka wygrywa z kaflem, a kafel
 * z domyślnym transhumeralnym. Od tej wartości zależy zestaw punktów pomiarowych na karcie
 * — pomyłka to inne punkty niż te, które protetyk mierzył, nie kosmetyka.
 */
export function resolveAmputationType(
	saved: AmputationType | null | undefined,
	fromTile: AmputationType | null | undefined,
): AmputationType {
	return saved ?? fromTile ?? AmputationType.TranshumeralAmputation;
}

/**
 * Czy pokazać pole „Select type of amputation" w szczegółach klienta.
 *
 * ŚCIEŻKA ACRO GO NIE POKAZUJE (decyzja Ownera 2026-10-07, zgłoszenie Alicji GLA-81, zgodnie
 * z dokumentem „Zmiany w I'AM App 09.26", krok 4). Powód nie jest kosmetyczny: przełączenie
 * tego pola na Transhumeral wysyłało produkcji zwykły `Glaze Arm` ZAMIAST kapy Acro, a karta
 * pomiarowa zaczynała prosić o wymiary kikuta ramienia, których pacjent po wyłuszczeniu nie ma.
 * Sprzeczność szła dalej do karty PDF, a aplikacja niczego nie walidowała.
 *
 * Wartość `shoulder_disarticulation` zapisuje się dalej jak dotąd — ukrywamy kontrolkę, nie pole.
 */
export function showsAmputationTypeField(
	isAbove: boolean,
	isShoulderDisarticulation: boolean,
): boolean {
	return isAbove && !isShoulderDisarticulation;
}
